/**
 * 장소 검색 기록 — localStorage 기반.
 * 모든 지도 화면 (일반 모임 PlaceSearch, 여행 모임 집결지/숙박지, MapPicker 등) 에서 공유.
 *
 * 저장 시점: 사용자가 검색 결과를 선택/확정했을 때.
 * 표시 시점: 검색창 focus 또는 입력 시 — 입력어와 유사한 기록을 결과 위에 보강.
 */

export interface PlaceHistoryItem {
    name: string;
    address?: string;
    lat?: number;
    lng?: number;
    /** 마지막 사용 시점 (ms). 정렬/만료용 */
    timestamp: number;
}

const KEY = 'placeSearchHistory';
const MAX_ITEMS = 30;

const safeParse = <T,>(raw: string | null, fallback: T): T => {
    if (!raw) return fallback;
    try { return JSON.parse(raw) as T; } catch { return fallback; }
};

export const getPlaceHistory = (): PlaceHistoryItem[] => {
    if (typeof localStorage === 'undefined') return [];
    return safeParse(localStorage.getItem(KEY), [] as PlaceHistoryItem[])
        .filter(h => !!h?.name);
};

/**
 * 검색 기록 추가/갱신. 동일 name 은 dedup 후 최상단으로 이동.
 */
export const addPlaceHistory = (item: Omit<PlaceHistoryItem, 'timestamp'>): void => {
    if (!item?.name) return;
    if (typeof localStorage === 'undefined') return;
    const list = getPlaceHistory();
    const filtered = list.filter(h => h.name !== item.name);
    const next: PlaceHistoryItem[] = [
        { ...item, timestamp: Date.now() },
        ...filtered,
    ].slice(0, MAX_ITEMS);
    try {
        localStorage.setItem(KEY, JSON.stringify(next));
    } catch { /* quota or private mode 등 — 무시 */ }
};

/**
 * 검색어와 유사한 기록 매칭 (substring, case-insensitive).
 * @param query 입력어. 비어있으면 최근 N개 반환.
 * @param limit 최대 반환 개수
 */
export const matchPlaceHistory = (query: string, limit: number = 5): PlaceHistoryItem[] => {
    const all = getPlaceHistory();
    const q = (query || '').trim().toLowerCase();
    if (!q) return all.slice(0, limit);
    return all
        .filter(h =>
            h.name.toLowerCase().includes(q)
            || (h.address && h.address.toLowerCase().includes(q)),
        )
        .slice(0, limit);
};

export const clearPlaceHistory = (): void => {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(KEY);
};
